<template>
  <Transition name="fade">
    <div v-if="loginOpen" class="modal-backdrop auth-backdrop" @click.self="loginOpen = false">
      <form class="login-modal auth-modal" :class="{ 'is-register': authMode === 'register' }" role="dialog" aria-modal="true" aria-labelledby="auth-modal-title" @submit.prevent="submitAuth">
        <button type="button" class="modal-close" aria-label="关闭" @click="loginOpen = false">×</button>
        <header class="auth-heading">
          <div class="auth-brand"><SiteBrand /></div>
          <h2 id="auth-modal-title">{{ authMode === 'login' ? '欢迎回来' : '创建新账号' }}</h2>
          <p>{{ authMode === 'login' ? '登录后体验完整服务' : '注册即刻开启精彩体验' }}</p>
        </header>
        <div class="auth-fields">
          <label class="auth-field">
            <span>账号</span>
            <input v-model.trim="authForm.account" autocomplete="username" required minlength="4" maxlength="64" placeholder="请输入账号" />
          </label>
          <label v-if="authMode === 'register'" class="auth-field">
            <span>手机号</span>
            <input v-model="authForm.phone" maxlength="30" placeholder="选填手机号" />
          </label>
          <label class="auth-field auth-field--wide">
            <span>密码</span>
            <div class="auth-password-input">
              <input id="auth-modal-password" ref="passwordInput" data-input-clear="off" v-model="authForm.password" required :type="passwordVisible ? 'text' : 'password'" :autocomplete="authMode === 'login' ? 'current-password' : 'new-password'" placeholder="请输入密码" />
              <div class="auth-password-actions">
                <button v-if="authForm.password" type="button" class="auth-password-clear" aria-label="清空密码" title="清空密码" @pointerdown.prevent @click="clearPassword">×</button>
                <button type="button" :aria-label="passwordVisible ? '隐藏密码' : '显示密码'" :title="passwordVisible ? '隐藏密码' : '显示密码'" aria-controls="auth-modal-password" :aria-pressed="passwordVisible" @click="passwordVisible = !passwordVisible"><SiteIcon :name="passwordVisible ? 'eye' : 'eye-slash'" :size="20" /></button>
              </div>
            </div>
          </label>
          <label v-if="authMode === 'register'" class="auth-field auth-field--invitation">
            <span>邀请来源</span>
            <select v-model="authForm.inviteKind">
              <option value="agent">代理邀请</option>
              <option value="member">用户邀请（邀请好友）</option>
              <option value="">自动识别（旧链接）</option>
            </select>
          </label>
          <label v-if="authMode === 'register'" class="auth-field auth-field--invitation">
            <span>{{ authForm.inviteKind === 'member' ? '用户邀请码' : authForm.inviteKind === 'agent' ? '代理邀请码' : '邀请码' }}</span>
            <input v-model.trim="authForm.inviteCode" maxlength="64" :placeholder="authForm.inviteKind === 'member' ? '用户邀请码（选填）' : authForm.inviteKind === 'agent' ? '代理邀请码（选填）' : '邀请码（选填）'" />
          </label>
          <label class="auth-field auth-field--wide">
            <span>图形验证码</span>
            <div class="captcha-input">
              <input v-model="authForm.captchaValue" required placeholder="请输入图中字符" />
              <button type="button" aria-label="刷新验证码" @click="loadCaptcha"><img v-if="captcha.base64" :src="captcha.base64" alt="图形验证码" /><span v-else>点击刷新</span></button>
            </div>
          </label>
        </div>
        <div v-if="authMode === 'login'" class="form-row">
          <label><input v-model="rememberAccount" type="checkbox" @change="clearRememberedAccount" /> 记住账号</label>
          <button type="button" @click="loginOpen = false; navigate('/user/password')">忘记密码？</button>
        </div>
        <button class="primary modal-submit" :disabled="authSubmitting">{{ authSubmitting ? '提交中…' : authMode === 'login' ? '登录' : '注册并登录' }}</button>
        <small>{{ authMode === 'login' ? '还没有账号？' : '已有账号？' }}<button type="button" @click="openAuth(authMode === 'login' ? 'register' : 'login')">{{ authMode === 'login' ? '立即注册' : '返回登录' }}</button></small>
      </form>
    </div>
  </Transition>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useAppContext } from '../../composables/appContext'
import SiteBrand from '../SiteBrand.vue'
import SiteIcon from '../SiteIcon.vue'
const { authForm, authMode, authSubmitting, captcha, isLoggedIn, loadCaptcha, loginOpen, navigate, openAuth, submitAuth } = useAppContext()
const rememberedKey = 'kg-web-remembered-account'
const rememberAccount = ref(false), passwordVisible = ref(false), passwordInput = ref(null)
function clearPassword() {
  authForm.value.password = ''
  passwordInput.value?.focus()
}
function clearRememberedAccount() { if (!rememberAccount.value) { try { localStorage.removeItem(rememberedKey) } catch { /* 私密模式下仍可登录 */ } } }
watch(loginOpen, open => {
  passwordVisible.value = false
  if (!open) return
  try {
    const saved = localStorage.getItem(rememberedKey) || ''
    rememberAccount.value = Boolean(saved)
    if (saved && !authForm.value.account) authForm.value.account = saved
  } catch { rememberAccount.value = false }
})
watch(isLoggedIn, loggedIn => {
  if (!loggedIn) return
  try {
    if (rememberAccount.value) localStorage.setItem(rememberedKey, authForm.value.account)
    else localStorage.removeItem(rememberedKey)
  } catch { /* 存储不可用不影响登录结果 */ }
})
</script>

<style scoped>
.login-modal .auth-password-input {
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 7px;
  overflow: hidden;
  border: 1px solid #dce7f2;
  border-radius: 11px;
  background: #f7faff;
}
.login-modal .auth-password-input > input {
  flex: 1;
  min-width: 0;
  width: 0;
  margin: 0;
  padding: 13px;
  border: 0;
  border-radius: 0;
  outline: none;
  box-shadow: none;
  background: transparent;
}
.login-modal .auth-password-input > input:focus,
.login-modal .auth-password-input > input:focus-visible {
  outline: none;
  box-shadow: none;
}
.auth-password-actions {
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: 4px;
  padding-right: 10px;
}
.auth-password-actions > button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  flex: none;
  align-self: stretch;
  padding: 0;
  background: transparent;
  color: #267eaf;
}
.auth-password-actions > .auth-password-clear {
  align-self: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e5ebf2;
  color: #78899f;
  font: 18px/1 Arial, sans-serif;
}
.auth-password-input button:focus-visible {
  outline: 2px solid #39aaf0;
  outline-offset: -4px;
  border-radius: 8px;
}
.login-modal .auth-password-input:focus-within {
  border-color: #39aaf0;
  outline: none;
  box-shadow: 0 0 0 3px rgba(42,164,239,.1);
}
.login-modal .captcha-input {
  align-items: stretch;
}
.login-modal .captcha-input > button {
  position: relative;
  height: auto;
  min-height: 0;
  border-radius: 11px;
}
.login-modal .captcha-input img {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.login-modal > label > input:focus-visible,
.login-modal .captcha-input > input:focus-visible {
  outline: none;
}
.login-modal select {
  width: 100%;
  min-height: 42px;
  padding: 8px 12px;
  border: 1px solid #dce3ee;
  border-radius: 8px;
  background: #fff;
  color: #23344f;
  font: inherit;
}
.login-modal select:focus-visible { outline: 2px solid #287dde; outline-offset: 2px; }

/* 注册字段成组排列，弹窗按内容自然展开，不再产生内部滚动区。 */
.auth-backdrop {
  z-index: 115;
  padding: 16px;
  align-items: safe center;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: none;
}
.auth-backdrop::-webkit-scrollbar { display: none; }
.login-modal.auth-modal {
  --auth-control-height: 44px;
  position: relative;
  width: min(440px, 100%);
  max-height: none;
  overflow: visible;
  padding: 28px;
  border: 1px solid rgba(220, 230, 242, .8);
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(8, 26, 48, .24);
}
.auth-modal.is-register { width: min(600px, 100%); }
.auth-modal .auth-brand {
  --brand-mark-size: 34px;
  --brand-word-height: 25px;
  --brand-gap: 9px;
}
.auth-heading h2 {
  margin: 12px 0 5px;
  color: #23344f;
  font-size: 26px;
  font-weight: 650;
  line-height: 1.3;
  letter-spacing: .3px;
}
.auth-heading p { margin: 0 0 24px; color: #8a98aa; text-align: center; font-size: 13px; line-height: 20px; }
.auth-fields { display: grid; gap: 14px 16px; }
.is-register .auth-fields { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.auth-field { display: grid; min-width: 0; gap: 6px; color: #4a5c72; font-size: 13px; line-height: 18px; }
.auth-field--wide { grid-column: 1 / -1; }
.auth-modal .auth-field input,
.auth-modal .auth-field select {
  min-width: 0;
  width: 100%;
  height: var(--auth-control-height);
  min-height: 0;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #dce7f2;
  border-radius: 10px;
  background: #f7faff;
  color: #334b69;
  font-size: 14px;
  line-height: 1.4;
  transition: border-color .18s, box-shadow .18s, background .18s;
}
.auth-modal .auth-field input:hover,
.auth-modal .auth-field select:hover { border-color: #b9cfe5; }
.login-modal.auth-modal .auth-field > select {
  height: var(--auth-control-height);
  min-height: 0;
  padding: 0 40px 0 12px;
}
.auth-modal .auth-field input:focus,
.auth-modal .auth-field select:focus { border-color: #39aaf0; outline: none; box-shadow: 0 0 0 3px rgba(42, 164, 239, .1); background: #fff; }
.auth-modal .auth-password-input { height: var(--auth-control-height); margin: 0; border-radius: 10px; }
.auth-modal .auth-field .auth-password-input > input { width: 0; height: 100%; padding: 0 12px; border: 0; border-radius: 0; background: transparent; box-shadow: none; }
.auth-modal .captcha-input { display: grid; grid-template-columns: minmax(0, 1fr) 132px; gap: 10px; margin: 0; }
.auth-modal .captcha-input > button { min-width: 0; height: var(--auth-control-height); border: 1px solid #dce7f2; border-radius: 10px; background: #f7faff; }
.auth-modal .modal-close { display: grid; place-items: center; top: 16px; right: 16px; width: 30px; height: 30px; padding: 0; border-radius: 50%; background: #f3f6fa; font-size: 22px; line-height: 1; transition: color .18s, background .18s; }
.auth-modal .modal-close:hover { color: #287bc8; background: #e8f2fc; }
.auth-modal .modal-submit { height: 44px; margin: 22px 0 14px; padding: 0 20px; border-radius: 10px; background: linear-gradient(110deg, #328bdd, #2674c7); box-shadow: 0 6px 16px rgba(38, 116, 199, .16); font-size: 15px; font-weight: 600; }
.auth-modal .modal-submit:hover:not(:disabled) { filter: brightness(1.04); }
.auth-modal > small { font-size: 13px; line-height: 20px; }
.auth-modal > small button { margin-left: 6px; font: inherit; }
.auth-modal .form-row { align-items: center; margin: 16px 0 0; font-size: 12px; }
.auth-modal .form-row > label { display: inline-flex; align-items: center; gap: 6px; }
.auth-modal .form-row > button { font: inherit; }
@media (max-width: 520px) {
  .auth-backdrop { padding: 12px; }
  .login-modal.auth-modal { padding: 24px 20px; border-radius: 18px; }
  .auth-fields { column-gap: 12px; }
  .is-register .auth-field--invitation { grid-column: 1 / -1; }
  .auth-modal .captcha-input { grid-template-columns: minmax(0, 1fr) 104px; gap: 8px; }
}
@media (max-height: 720px) {
  .login-modal.auth-modal { --auth-control-height: 40px; padding: 20px; }
  .auth-modal .auth-brand { --brand-mark-size: 30px; --brand-word-height: 22px; }
  .auth-heading h2 { margin-top: 10px; font-size: 24px; }
  .auth-heading p { margin-bottom: 18px; }
  .auth-fields { row-gap: 10px; }
  .auth-field { gap: 5px; }
  .auth-modal .modal-submit { height: 40px; margin: 18px 0 12px; }
}
@media (max-height: 600px) {
  .login-modal.auth-modal { --auth-control-height: 36px; padding: 16px; }
  .auth-modal .auth-brand { --brand-mark-size: 28px; --brand-word-height: 20px; }
  .auth-heading h2 { margin: 8px 0 4px; font-size: 22px; }
  .auth-heading p { margin-bottom: 14px; font-size: 12px; line-height: 18px; }
  .auth-fields { row-gap: 8px; }
  .auth-field { gap: 4px; font-size: 12px; line-height: 16px; }
  .auth-modal .modal-submit { height: 38px; margin: 14px 0 10px; }
  .auth-modal > small { font-size: 12px; line-height: 18px; }
}
@media (max-height: 520px) {
  .login-modal.auth-modal { --auth-control-height: 32px; padding: 12px 20px; }
  .auth-heading .auth-brand, .auth-heading p { display: none; }
  .auth-heading h2 { margin: 0 0 12px; font-size: 22px; line-height: 26px; }
  .auth-field { line-height: 14px; }
  .is-register .auth-field--invitation { grid-column: auto; }
  .auth-modal .modal-submit { height: 32px; margin: 12px 0 8px; font-size: 14px; }
  .auth-modal > small { line-height: 16px; }
  .auth-modal .modal-close { top: 8px; right: 10px; width: 28px; height: 28px; }
}
</style>
